import { canonicalGalleryDomain } from '@/lib/templates/gallery-domain'

/**
 * A flow's URL identity is its canonical gallery domain (`notion.com`): one
 * flow per brand, the same key `templateBrands` and the gallery's `?brand=`
 * filter use, and reversible.
 *
 * Two TLDs are refused because the path would collide with reserved URL
 * suffixes: `.zip` sits in the proxy matcher's asset-extension exclusion
 * (`proxy.ts`), and `.md` is the Markdown-twin rewrite lane
 * (`lib/site/seo/markdown-paths.ts`).
 */
export const RESERVED_FLOW_SLUG_SUFFIXES: ReadonlyArray<string> = ['zip', 'md']

/** RFC 1035 hostname ceiling; anything longer is not a domain. */
const MAX_FLOW_SLUG_LENGTH = 253

export function hasReservedFlowSlugSuffix(domain: string): boolean {
  const suffix = domain.slice(domain.lastIndexOf('.') + 1)
  return RESERVED_FLOW_SLUG_SUFFIXES.includes(suffix)
}

/** The slug for a brand domain, or `''` when it cannot be a public flow URL. */
export function flowTemplateSlug(domain: string): string {
  const canonical = canonicalGalleryDomain(domain)
  if (!canonical || hasReservedFlowSlugSuffix(canonical)) {
    return ''
  }
  return canonical
}

/** Guard for a slug that arrived from a URL or a request body. */
export function isValidFlowTemplateSlug(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length > 0 &&
    value.length <= MAX_FLOW_SLUG_LENGTH &&
    flowTemplateSlug(value) === value
  )
}

/** The signed-in flows gallery (rendered inside the app shell). */
export const FLOWS_PATH = '/flows'
/** The public mirror of the flows gallery: the crawlable canonical. */
export const PUBLIC_FLOWS_PATH = '/browse/flows'

/** `/flows` inside the app, `/browse/flows` on the public and marketing surfaces. */
export function flowsBasePathForSurface(
  surface: 'app' | 'public' | 'marketing'
): string {
  return surface === 'app' ? FLOWS_PATH : PUBLIC_FLOWS_PATH
}

/** `/flows/<slug>`, or the public mirror when `basePath` is `/browse/flows`. */
export function publicFlowTemplatePath(
  slug: string,
  basePath: string = FLOWS_PATH
): string {
  return `${basePath.replace(/\/$/, '')}/${slug}`
}

/** The flows gallery with an optional committed search query. */
export function buildFlowsUrl(options: {
  q?: string
  basePath?: string
}): string {
  const base = (options.basePath ?? FLOWS_PATH).replace(/\/$/, '')
  const query = options.q?.trim()
  if (!query) {
    return base
  }
  const params = new URLSearchParams()
  params.set('q', query)
  return `${base}?${params.toString()}`
}
